import axios from 'axios';
import fileHelper from './helpers/file';
import apiClient from 'shared/data/helpers/ApiClient';

export default {
  /**
   * Upload an asset for a team
   */
  uploadAsset(teamId, eventId, file) {
    return fileHelper.upload(
      '/api/upload/event.uploadAsset',
      { teamId, eventId },
      file,
    );
  },

  /**
   * Get events associated with global element
   *
   */
  getByGlobalElement(teamId, showId, itemSetId, rowId) {
    return axios
      .post('/api/event.getByGlobalElement', {
        params: { teamId, showId, itemSetId, rowId },
      })
      .then((res) => res.data);
  },

  /**
   * Get global element manager event for a team
   *
   */
  getGlobalElementManager(teamId) {
    return axios
      .post('/api/event.getGlobalElementManagerByTeamId', {
        params: { teamId },
      })
      .then((res) => res.data);
  },

  /**
   * Get global element manager event element sets
   */
  getGlobalElementManagerElementSets(teamId, eventId) {
    return axios
      .post('/api/event.getGlobalElementManagerElementSets', {
        params: {
          teamId: teamId,
          eventId: eventId,
        },
      })
      .then((res) => {
        const data = res.data;
        // API returns { eventId, shows }; legacy v4 expects an array of shows
        let shows;
        if (Array.isArray(data)) {
          shows = data;
        } else if (data && Array.isArray(data.shows)) {
          shows = data.shows;
        } else {
          shows = [];
        }
        // v2 Lexical sets (displayType gem) share the GEM event; legacy v4 flows only support v1
        return shows.filter((show) => show.displayType !== 'gem');
      });
  },

  getGlobalElementManagerElementSetRowLabelCounts(teamId, eventId) {
    return axios
      .post('/api/event.getGlobalElementManagerElementSetRowLabelCounts', {
        params: {
          teamId: teamId,
          eventId: eventId,
        },
      })
      .then((res) => res.data);
  },

  /**
   * Get global element manager event for a team
   *
   */
  getGlobalElementLabels(teamId, eventId) {
    return axios
      .post('/api/event.getGlobalElementLabels', {
        params: { teamId, eventId },
      })
      .then((res) => res.data);
  },

  /**
   * Create a globale element label for event
   */
  createGlobalElementLabel(teamId, eventId, payload) {
    return axios
      .post('/api/event.createGlobalElementLabel', {
        params: { teamId, eventId },
        payload,
      })
      .then((res) => res.data);
  },

  /**
   * Remove event global element label
   */
  deleteGlobalElementLabel(teamId, eventId, labelId) {
    return axios
      .post('/api/event.deleteGlobalElementLabel', {
        params: { teamId, eventId, labelId },
      })
      .then((res) => res.data);
  },

  migrateAllEventShowCategoriesToEventGlobalElementLabels(teamId, eventId) {
    return apiClient().event.migrateAllEventShowCategoriesToEventGlobalElementLabels(
      teamId,
      eventId,
    );
  },

  /**
   * Update event label
   */
  updateLabel(teamId, eventId, labelId, changes) {
    return axios
      .post('/api/event.updateLabel', {
        params: { teamId, eventId, labelId },
        payload: changes,
      })
      .then((res) => res.data);
  },

  /**
   * Get an event
   *
   */
  get(teamId, eventId) {
    return axios
      .post('/api/event.get', {
        params: {
          teamId: teamId,
          eventId: eventId,
        },
      })
      .then((res) => res.data);
  },

  /**
   * Get an event for guest pass
   *
   */
  getForGuest(teamId, eventId) {
    return axios
      .post('/api/event.getForGuest', {
        params: {
          teamId: teamId,
          eventId: eventId,
        },
      })
      .then((res) => res.data);
  },

  /**
   * Create an event
   *
   */
  create(teamId, event) {
    return axios
      .post('/api/event.create', {
        params: { teamId },
        payload: { event },
      })
      .then((res) => res.data);
  },

  /**
   * Create an event from an event product
   *
   */
  createFromProduct(teamId, productId, event) {
    return axios
      .post('/api/event.createFromProduct', {
        params: {
          teamId: teamId,
          eventProductId: productId,
        },
        payload: event,
      })
      .then((res) => res.data);
  },

  /**
   * Get event products
   */
  getProducts() {
    return axios.post('/api/eventProduct.getAll').then((res) => res.data);
  },

  /**
   * Get events by user ID
   */
  getByUserId(userId, query) {
    return axios
      .post('/api/event.getByUserId', {
        params: { userId },
        query,
      })
      .then((res) => res.data);
  },

  /**
   * Get events by team ID
   */
  getByTeamId(teamId, query) {
    return axios
      .post('/api/event.getByTeamId', {
        params: { teamId },
        query,
      })
      .then((res) => res.data);
  },

  /**
   * Get events by team ID with shows populated
   */
  getByTeamIdWithShows(teamId, query) {
    return axios
      .post('/api/event.getByTeamIdWithShows', {
        params: { teamId },
        query,
      })
      .then((res) => res.data);
  },

  /**
   * Get all unique labels off of events that a user is associated with
   */
  getAllLabelsByUserId(userId, query) {
    return axios
      .post('/api/event.getAllLabelsByUserId', {
        params: { userId },
        query,
      })
      .then((res) => res.data);
  },

  /**
   * Get all unique owners off of events that a user is associated with
   */
  getAllOwnersByUserId(userId, query) {
    return axios
      .post('/api/event.getAllOwnersByUserId', {
        params: { userId },
        query,
      })
      .then((res) => res.data);
  },

  /**
   * Create a crew member (AKA an event role)
   *
   */
  createRole(teamId, eventId, userId, role) {
    return axios
      .post('/api/event.createRole', {
        params: { teamId, eventId },
        payload: { userId, role },
      })
      .then((res) => res.data);
  },

  /**
   * Update (change) the role for an event+user pair
   *
   */
  updateRole(teamId, eventId, userId, roleId, role) {
    return axios
      .post('/api/event.updateRole', {
        params: {
          teamId: teamId,
          eventId: eventId,
          roleId: roleId,
        },
        payload: {
          userId: userId,
          role: role,
        },
      })
      .then((res) => res.data);
  },

  /**
   * Remove a user from an event
   *
   */
  removeRole(teamId, eventId, userId) {
    return axios
      .post('/api/event.deleteRole', {
        params: {
          userId: userId,
          eventId: eventId,
          teamId: teamId,
        },
      })
      .then((res) => res.data);
  },

  /**
   * Resend an invite email
   *
   */
  resendInviteEmail(teamId, eventId, userId, customInviteMessage) {
    return axios
      .post('/api/event.resendInviteEmail', {
        params: {
          userId: userId,
          eventId: eventId,
          teamId: teamId,
        },
        payload: { customInviteMessage },
      })
      .then((res) => res.data);
  },

  /**
   * Get event roles/crew by role type
   *
   */
  getRolesByType(teamId, eventId, role) {
    return axios
      .post('/api/event.getRolesByType', {
        params: {
          teamId,
          eventId,
          role,
        },
      })
      .then((res) => res.data);
  },

  /**
   * Get event roles/crew
   *
   */
  getRoles(teamId, eventId) {
    return axios
      .post('/api/event.getRoles', {
        params: { teamId, eventId },
      })
      .then((res) => res.data);
  },

  /**
   * Get list of event role types
   */
  getRoleTypes() {
    // Empty payload obj sent to fix IE "Unsupported Media Type" error
    return axios
      .post('/api/event.getRoleTypes', {
        payload: {},
      })
      .then((res) => res.data);
  },

  /**
   * Update an event
   */
  update(team, eventId, changes) {
    let teamId = teamTypeConverter(team);

    return axios
      .post('/api/event.update', {
        params: {
          teamId: teamId,
          eventId: eventId,
        },
        payload: changes,
      })
      .then((res) => res.data);
  },

  /**
   * Update an event owner
   */
  updateOwner(teamId, eventId, owner) {
    return axios
      .post('/api/event.updateOwner', {
        params: {
          teamId: teamId,
          eventId: eventId,
        },
        payload: { owner },
      })
      .then((res) => res.data);
  },

  /**
   * Update event labels
   */
  updateLabels(teamId, eventId, labels) {
    return axios
      .post('/api/event.updateLabels', {
        params: { teamId, eventId },
        payload: { labels },
      })
      .then((res) => res.data);
  },

  /**
   * Soft delete an event
   */
  softDelete(teamId, eventId) {
    return axios
      .post('/api/event.softDelete', {
        params: { teamId, eventId },
      })
      .then((res) => res.data);
  },

  /**
   * Restore a deleted event
   */
  restore(teamId, eventId) {
    return axios
      .post('/api/event.restore', {
        params: {
          teamId: teamId,
          eventId: eventId,
        },
      })
      .then((res) => res.data);
  },

  duplicate(teamId, eventId, title, startOn, endOn) {
    return axios
      .post('/api/event.duplicate', {
        params: { teamId, eventId },
        payload: { title, startOn, endOn },
      })
      .then((res) => res.data);
  },

  /**
   * Update event photo
   */
  updatePhoto(team, eventId, file) {
    let teamId = teamTypeConverter(team);

    return fileHelper.upload(
      '/api/upload/event.updatePhoto',
      { teamId, eventId },
      file,
    );
  },
};

function teamTypeConverter(team) {
  return typeof team === 'string' ? team : team.get('_id');
}
